import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DnD_TYPES } from "./DnDUtils";
import { CdmDestinationGroup } from "userful-chronos-app-common-js/dist/models/cdm/CdmGroup";
import { CdmPhysicalAudioDest, CdmPhysicalVideoDest } from "userful-chronos-app-common-js/dist/models/cdm/CdmPhysical";
import { CdmVideoWallDest } from "userful-chronos-app-common-js/dist/models/cdm/CdmVideoWall";
import {
    AnyDestination,
    AnyMapper,
    DestinationType,
} from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import DestinationGroup from "./destinationwidigets/DestinationGroup";
import DestinationVideoWall from "./destinationwidigets/DestinationVideoWall";
import DestinationDisplay from "./destinationwidigets/DestinationDisplay";
import DestinationAudio from "./destinationwidigets/DestinationAudio";
import { APP_IDS, StringID } from "userful-chronos-app-common-js/dist/models/common";
import { useDraggable } from "@dnd-kit/core";
import { faCircleNotch, faXmark } from "@fortawesome/pro-regular-svg-icons";
import "./../../Components/card/UserfulInfinityCard.scss";
import { useTranslation } from "react-i18next";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import {
    getUsageForPhysicalDestination,
    getUsageForVideoWallDestination,
} from "userful-chronos-app-common-js/dist/models/cdm/DestinationsUtils";
import UserfulTooltip from "../../Widgets/UserfulTooltip";
import "./DestinationDraggable.scss";
import { useMappingSelector } from "userful-chronos-common-store/dist/mapping";
import DestinationCardMenu from "./menu/DestinationCardMenu";
export interface DestinationDraggableData {
    draggingType: keyof typeof DnD_TYPES;
    destinationType: DestinationType;
    item: AnyDestination | null;
    fromMappingGroup?: StringID;
}

interface IProps {
    item: AnyDestination | null;
    dragID: string;
    destinationType: DestinationType;
    onDelete?: Function;
    currentMappingGroup?: AnyMapper;
    appID?: string;
    isMoving?: boolean; // meaning dest is being moved from one mapper to another, need to wait until op is complete
    isDragging?: boolean;
    disableInUse?: boolean;
    canExecuteMapper?: string;
    itemIndex?: number;
    isNotification?: boolean;
}

export function DestinationDraggableInner({
    item,
    destinationType,
    onDelete,
    currentMappingGroup,
    appID,
    isMoving,
    isDragging,
    ...props
}: IProps) {
    const [hover, setIsHover] = useState(false);

    const { t } = useTranslation();

    const handleMouseEnter = (value) => {
        setIsHover(value);
    };

    const handleMouseLeave = (value) => {
        setIsHover(value);
    };

    const usage = props.disableInUse
        ? ""
        : (destinationType === "videoWalls" &&
              getUsageForVideoWallDestination(item as CdmVideoWallDest, currentMappingGroup)) ||
          ((destinationType === "physicalVideo" || destinationType === "physicalAudio") &&
              getUsageForPhysicalDestination(item as CdmPhysicalVideoDest, currentMappingGroup)) ||
          "";
    const inUseTooltip = props.disableInUse
        ? ""
        : (destinationType === "videoWalls" &&
              usage &&
              t("CommonUI.MappingGroups.videoWallInUse", { usage: usage, interpolation: { escapeValue: false } })) ||
          (destinationType === "physicalVideo" &&
              usage &&
              t("CommonUI.MappingGroups.inUse", { usage: usage, interpolation: { escapeValue: false } })) ||
          (destinationType === "physicalAudio" &&
              usage &&
              t("CommonUI.MappingGroups.inUseAudio", { usage: usage, interpolation: { escapeValue: false } })) ||
          "";

    const destinationCard = () => (
        <div
            className={`usefulCard destination-card destination-type-${destinationType} ${item.availability === "NOT_CONNECTED" ? "offline" : ""}`}
        >
            {destinationType === "groups" && (
                <DestinationGroup
                    item={item as CdmDestinationGroup}
                    itemID={item.id}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                />
            )}
            {destinationType === "videoWalls" && (
                <DestinationVideoWall
                    item={item as CdmVideoWallDest}
                    itemID={item.id}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                    inUse={inUseTooltip}
                />
            )}
            {destinationType === "physicalVideo" && (
                <DestinationDisplay
                    item={item as CdmPhysicalVideoDest}
                    itemID={item.id}
                    handleMouseEnter={handleMouseEnter}
                    handleMouseLeave={handleMouseLeave}
                    inUse={inUseTooltip}
                />
            )}
            {destinationType === "physicalAudio" && (
                <DestinationAudio item={item as CdmPhysicalAudioDest} itemID={item.id} inUse={inUseTooltip} />
            )}
            {isMoving && (
                <div className="movingOverlay">
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                </div>
            )}
        </div>
    );

    return !hover && inUseTooltip && !isDragging ? (
        <UserfulTooltip placement="top" title={inUseTooltip}>
            {destinationCard()}
        </UserfulTooltip>
    ) : (
        destinationCard()
    );
}

export default function DestinationDraggable(props: IProps & { index: string }) {
    const { t } = useTranslation();
    const userPermission = getGlobalStates().userPermission as UserPermission;

    const busyDestinations = useMappingSelector((state) => state.mappingGroupStore.busyDestinations);
    const isBusy = busyDestinations.findIndex((d) => d.value === props.item?.id.value) >= 0;
    const canDrag =
        (userPermission.isMapperCreator || props.isNotification) && props.item && !isBusy && !props.isMoving;

    const { attributes, listeners, setNodeRef } = useDraggable({
        id: props.dragID,
        data: {
            draggingType: DnD_TYPES.destination,
            destinationType: props.destinationType,
            item: props.item,
            fromMappingGroup: props.currentMappingGroup?.id,
        } as DestinationDraggableData,
    });

    // check if the app is sapphire and if it is, it should be sapphire admin
    const getSapphirePermission =
        props.appID !== APP_IDS.Spaces ||
        (props.appID === APP_IDS.Spaces && getGlobalStates().userPermission.isSapphireAdmin);
    const canDelete =
        props.isNotification ||
        (props.onDelete &&
            !isBusy &&
            (!props.canExecuteMapper || props.canExecuteMapper === "canExecute") &&
            getSapphirePermission);

    const [visible, setVisible] = useState(props.itemIndex && props.itemIndex > 20 ? false : true);
    const visibleRef = useRef(visible);
    useEffect(() => {
        visibleRef.current = visible;
    }, [visible]);
    const elementRef = useCallback((element: HTMLDivElement) => {
        let observer;
        if (element) {
            observer = new IntersectionObserver(
                (entries, opts) => {
                    const interacted = entries.length > 0 && entries[entries.length - 1].isIntersecting;
                    if (visibleRef.current !== interacted) {
                        setVisible(interacted);
                    }
                },
                {
                    root: element.parentElement, // default is the viewport
                    threshold: 0.01, // percentage of target's visible area. Triggers "onIntersection"
                },
            );
            observer.observe(element);
        }
        return () => observer && observer.disconnect();
    }, []);

    return (
        <div className="destinationOutterConatiner" id={`destination-draggable-${props.index}`} ref={elementRef}>
            {visible && (
                <>
                    <div
                        ref={canDrag ? setNodeRef : null}
                        {...(canDrag ? listeners : {})}
                        {...(canDrag ? attributes : {})}
                    >
                        <DestinationDraggableInner
                            item={props.item}
                            dragID={props.dragID}
                            destinationType={props.destinationType}
                            onDelete={props.onDelete}
                            currentMappingGroup={props.currentMappingGroup}
                            appID={props.appID}
                            isMoving={props.isMoving}
                        />
                    </div>
                    {canDelete && (
                        <div className="sourceContainerOverlay" onClick={() => props.onDelete()}>
                            <UserfulTooltip placement="bottom" title={t("CommonUI.MappingGroups.removeDestination")}>
                                <div className="iconButton" style={{ cursor: "pointer" }}>
                                    <FontAwesomeIcon icon={faXmark} className="fontAwesomeCustomIcon" />
                                </div>
                            </UserfulTooltip>
                        </div>
                    )}
                    {getSapphirePermission && (
                        <DestinationCardMenu
                            destination={props.item}
                            type={props.destinationType}
                            className={canDelete ? "" : "floatRight"}
                        />
                    )}
                    {isBusy && (
                        <div className="destinationBusy">
                            <FontAwesomeIcon icon={faCircleNotch} spin />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
